import FaviconHead from '@components/head/favicons';
import HreflangHead from '@components/head/hreflangHead';
import OnboardingMetaTagHead from '@components/head/onboardingMetadata';
import {
    ADDITIONAL_PRODUCT,
    COMMON,
    CONSENTS,
    FORM,
    ONBOARDING,
    SECONDS_PER_DAY,
} from '@constants';
import { EventTriggerSource } from '@enums';
import {
    GetOnboardingByCompanyAndProductDocument,
    GetOnboardingByCompanyAndProductQuery,
    OnboardingFragment,
} from '@graphql/generated/graphql';
import { useOnboarding } from '@hooks';
import { Flow } from '@lib/pimster-flow/components/Flow';
import { PageProps } from '@types';
import { buildHreflangArray } from '@utils/hreflang';
import {
    withApollo,
    withCompany,
    withErrorsGSP,
    withProduct,
} from '@utils/serverMethodPlugins';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

interface Props extends PageProps {
    onboarding: OnboardingFragment;
}

const OnboardingPage: NextPage<Props> = ({
    onboarding,
    hrefLang,
    serverCompanyContext,
    serverProductContext,
}) => {
    const { setOnboardingHasBeenSeen } = useOnboarding({
        productSlug: serverProductContext.meta.Slug!,
    });

    return (
        <>
            <OnboardingMetaTagHead onboarding={onboarding} />
            <FaviconHead productMeta={serverProductContext.meta} />
            <HreflangHead
                companySlug={serverCompanyContext.meta.Slug!}
                defaultLocale={serverCompanyContext.meta.DefaultLocale!}
                refs={hrefLang}
            />
            <Flow
                module={onboarding}
                triggerSource={EventTriggerSource.ONBOARDING}
                onExit={setOnboardingHasBeenSeen}
            />
        </>
    );
};

export const getStaticPaths: GetStaticPaths = () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};

export const getStaticProps: GetStaticProps<Props> = withErrorsGSP<Props>(
    withApollo<Props>(
        withCompany<Props>(
            withProduct<Props>(
                async ({ apolloClient, company, product, locale }) => {
                    const {
                        data: { products },
                    } =
                        await apolloClient.query<GetOnboardingByCompanyAndProductQuery>(
                            {
                                query: GetOnboardingByCompanyAndProductDocument,
                                variables: {
                                    companySlug: company.meta.Slug,
                                    productSlug: product.meta.Slug,
                                    locale,
                                },
                            }
                        );

                    const onboarding = products?.[0].Onboarding;

                    if (!onboarding) return { notFound: true };

                    return {
                        props: {
                            onboarding,
                            hrefLang: buildHreflangArray({
                                entity: onboarding,
                                currentLocale: locale!,
                                basePath: `/${product.meta.Slug}/onboarding`,
                            }),
                            ...(await serverSideTranslations(locale!, [
                                ADDITIONAL_PRODUCT,
                                COMMON,
                                CONSENTS,
                                FORM,
                                ONBOARDING,
                            ])),
                        },
                        revalidate: SECONDS_PER_DAY,
                    };
                }
            )
        )
    )
);

export default OnboardingPage;
